import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Plus, Trash2, Edit3, Globe, Server, Shield, Key, CreditCard, Package, CalendarClock } from "lucide-react";
import api, { apiError } from "../lib/api";
import { fmtEUR } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";

const TIPO_META = {
  dominio: { label: "Dominio", icon: Globe, color: "text-blue-600" },
  hosting: { label: "Hosting", icon: Server, color: "text-purple-600" },
  ssl: { label: "SSL", icon: Shield, color: "text-emerald-600" },
  licencia: { label: "Licencia", icon: Key, color: "text-amber-600" },
  suscripcion: { label: "Suscripción", icon: CreditCard, color: "text-pink-600" },
  otro: { label: "Otro", icon: Package, color: "text-gray-600" },
};

const PERIODICIDADES = ["mensual", "trimestral", "semestral", "anual", "unica"];

function empty() {
  return {
    tipo: "dominio", nombre: "", proveedor: "",
    coste_eur: 0, fecha_vencimiento: new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10),
    periodicidad: "anual", auto_renovacion: false, notas: "",
  };
}

export default function Renovaciones() {
  const [items, setItems] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(empty());
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/renovaciones");
      setItems(data.items || []);
      setTipos(data.tipos || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEdit(empty()); setEditingId(null); setOpen(true); };
  const openEdit = (r) => {
    setEdit({ ...r });
    setEditingId(r.id);
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editingId) {
        await api.put(`/renovaciones/${editingId}`, edit);
        toast.success("Actualizada");
      } else {
        await api.post("/renovaciones", edit);
        toast.success("Renovación creada");
      }
      setOpen(false);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta renovación?")) return;
    try {
      await api.delete(`/renovaciones/${id}`);
      toast.success("Eliminada");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const total_anual = items.reduce((sum, r) => {
    const factor = { mensual: 12, trimestral: 4, semestral: 2, anual: 1, unica: 0 }[r.periodicidad] || 1;
    return sum + (r.coste_eur || 0) * factor;
  }, 0);

  const proximas = items.filter((r) => r.dias_restantes !== null && r.dias_restantes >= 0 && r.dias_restantes <= 30).length;
  const vencidas = items.filter((r) => r.vencida).length;

  return (
    <div className="space-y-6" data-testid="renovaciones-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-3">
            <CalendarClock size={26} /> Renovaciones
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Dominios, hosting, SSL, licencias y suscripciones con alertas de vencimiento.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="reno-add-btn"><Plus size={14} className="mr-2" /> Nueva renovación</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editingId ? "Editar renovación" : "Nueva renovación"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Tipo</label>
                  <Select value={edit.tipo} onValueChange={(v) => setEdit({ ...edit, tipo: v })}>
                    <SelectTrigger data-testid="reno-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>{tipos.map((t) => <SelectItem key={t} value={t}>{TIPO_META[t]?.label || t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Periodicidad</label>
                  <Select value={edit.periodicidad} onValueChange={(v) => setEdit({ ...edit, periodicidad: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PERIODICIDADES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Nombre</label>
                <Input value={edit.nombre} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} data-testid="reno-nombre" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Proveedor</label>
                  <Input value={edit.proveedor} onChange={(e) => setEdit({ ...edit, proveedor: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Coste €</label>
                  <Input type="number" step="0.01" value={edit.coste_eur} onChange={(e) => setEdit({ ...edit, coste_eur: parseFloat(e.target.value) || 0 })} data-testid="reno-coste" />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Fecha vencimiento</label>
                <Input type="date" value={edit.fecha_vencimiento} onChange={(e) => setEdit({ ...edit, fecha_vencimiento: e.target.value })} data-testid="reno-fecha" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={!!edit.auto_renovacion} onChange={(e) => setEdit({ ...edit, auto_renovacion: e.target.checked })} />
                <label className="text-sm">Auto-renovación</label>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Notas</label>
                <Input value={edit.notas} onChange={(e) => setEdit({ ...edit, notas: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} disabled={!edit.nombre} data-testid="reno-save">{editingId ? "Guardar" : "Crear"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Total renovaciones" value={items.length} />
        <Kpi label="Próximas (≤30 días)" value={proximas} accent={proximas > 0 ? "amber" : ""} />
        <Kpi label="Vencidas" value={vencidas} accent={vencidas > 0 ? "red" : ""} />
        <Kpi label="Coste anualizado" value={fmtEUR(total_anual)} />
      </div>

      <div className="rounded-2xl border border-border bg-card">
        <div className="p-5 pb-3 font-semibold flex items-center gap-2">Listado <Button size="sm" variant="outline" onClick={load} disabled={loading}><RefreshCw size={12} className={loading ? "animate-spin" : ""} /></Button></div>
        {items.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center">Ninguna renovación registrada.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground tracking-widest">
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-4">Tipo</th>
                  <th className="text-left py-2 px-4">Nombre</th>
                  <th className="text-left py-2 px-4">Proveedor</th>
                  <th className="text-right py-2 px-4">Coste</th>
                  <th className="text-left py-2 px-4">Periodicidad</th>
                  <th className="text-left py-2 px-4">Vence</th>
                  <th className="text-right py-2 px-4"></th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {items.map((r) => {
                  const M = TIPO_META[r.tipo] || TIPO_META.otro;
                  const Icon = M.icon;
                  const status = r.vencida ? "danger" : r.dias_restantes !== null && r.dias_restantes <= 30 ? "warning" : "ok";
                  const statusColor = status === "danger" ? "text-red-600" : status === "warning" ? "text-amber-600" : "text-muted-foreground";
                  return (
                    <tr key={r.id} className="border-b border-border last:border-0" data-testid={`reno-row-${r.id}`}>
                      <td className="py-2 px-4"><span className={`inline-flex items-center gap-1.5 ${M.color}`}><Icon size={14} /> {M.label}</span></td>
                      <td className="py-2 px-4 font-semibold">{r.nombre}</td>
                      <td className="py-2 px-4 text-muted-foreground">{r.proveedor || "—"}</td>
                      <td className="py-2 px-4 text-right">{fmtEUR(r.coste_eur)}</td>
                      <td className="py-2 px-4"><Badge variant="outline">{r.periodicidad}</Badge></td>
                      <td className={`py-2 px-4 ${statusColor}`}>
                        <div>{r.fecha_vencimiento}</div>
                        <div className="text-[10px]">{r.vencida ? "VENCIDA" : r.dias_restantes === 0 ? "HOY" : r.dias_restantes !== null ? `en ${r.dias_restantes} días` : ""}</div>
                      </td>
                      <td className="py-2 px-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`reno-edit-${r.id}`}><Edit3 size={12} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(r.id)} data-testid={`reno-del-${r.id}`}><Trash2 size={12} /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  const c = accent === "amber" ? "text-amber-600" : accent === "red" ? "text-red-600" : "";
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-2 font-heading text-2xl font-extrabold tracking-tight tabular-nums ${c}`}>{value}</div>
    </div>
  );
}
